import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Handshake, ShieldAlert, Loader2, ExternalLink, Link2, DollarSign, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const AFFILIATES_URL = 'https://affiliates.voxinity.ai';

const PortalCard = ({ isDarkMode }) => (
  <div className="absolute inset-0 flex items-center justify-center p-6 overflow-y-auto">
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className={`max-w-lg w-full text-center rounded-3xl border p-10 ${
        isDarkMode ? 'bg-space-800/60 border-white/5' : 'bg-white border-gray-200'
      }`}
      data-testid="affiliates-portal-card"
    >
      <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center ${
        isDarkMode ? 'bg-teal-400/10' : 'bg-teal-500/10'
      }`}>
        <Handshake size={30} className="text-teal-400" />
      </div>
      <h2 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Voxinity Affiliate Program
      </h2>
      <p className={`text-sm leading-relaxed mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Refer agencies and creators to Voxinity and earn recurring commissions on every
        subscription. Track clicks, signups and payouts in your dedicated portal.
      </p>
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { icon: Link2, label: 'Your referral link' },
          { icon: Users, label: 'Signup tracking' },
          { icon: DollarSign, label: 'Recurring payouts' },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className={`rounded-2xl border px-3 py-4 ${
            isDarkMode ? 'border-white/5 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'
          }`}>
            <Icon size={16} className="text-teal-400 mx-auto mb-2" />
            <p className={`text-[11px] leading-tight ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
          </div>
        ))}
      </div>
      <button
        onClick={() => window.open(AFFILIATES_URL, '_blank', 'noopener')}
        className="w-full mb-3 px-5 py-3 rounded-xl text-sm font-semibold bg-teal-400 text-space-900 hover:bg-teal-300 transition-colors inline-flex items-center justify-center gap-2"
        data-testid="affiliates-open-portal-btn"
      >
        <ExternalLink size={15} />
        Open Affiliate Portal
      </button>
      <button
        onClick={() => { navigator.clipboard.writeText(AFFILIATES_URL); toast.success('Portal link copied'); }}
        className={`w-full px-5 py-2.5 rounded-xl text-xs font-medium border transition-colors ${
          isDarkMode ? 'border-white/10 text-gray-400 hover:text-white hover:border-white/25' : 'border-gray-200 text-gray-500 hover:text-gray-800'
        }`}
        data-testid="affiliates-copy-link-btn"
      >
        Copy portal link
      </button>
      <p className={`mt-6 text-[11px] leading-relaxed ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
        <TrendingUp size={11} className="inline mr-1 -mt-0.5" />
        The portal opens in a secure tab — the affiliate platform doesn't allow embedding inside other apps.
      </p>
    </motion.div>
  </div>
);

export default function AffiliatesPage() {
  const { workspaceId } = useParams();
  const { api, user, isAdmin } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [access, setAccess] = useState('loading'); // loading | granted | denied
  const [embed, setEmbed] = useState('checking'); // checking | iframe | portal
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (isAdmin) { setAccess('granted'); return; }
      try {
        const resp = await api.get('/api/workspaces');
        const ws = (resp.data || []).find((w) => w.workspace_id === workspaceId);
        const allowed = ws && (
          ['owner', 'admin'].includes(ws.permission_level) ||
          ws.owner_id === user?.user_id
        );
        if (!cancelled) setAccess(allowed ? 'granted' : 'denied');
      } catch {
        if (!cancelled) setAccess('denied');
      }
    };
    check();
    return () => { cancelled = true; };
  }, [api, workspaceId, user, isAdmin]);

  useEffect(() => {
    if (access !== 'granted') return;
    let cancelled = false;
    api.get(`/api/utils/embed-check?url=${encodeURIComponent(AFFILIATES_URL)}`)
      .then((r) => { if (!cancelled) setEmbed(r.data?.embeddable ? 'iframe' : 'portal'); })
      .catch(() => { if (!cancelled) setEmbed('portal'); });
    return () => { cancelled = true; };
  }, [access, api]);

  return (
    <DashboardLayout>
      <div
        className="relative w-full overflow-hidden embed-frame-wrap"
        data-testid="affiliates-page"
      >
        {(access === 'loading' || (access === 'granted' && embed === 'checking')) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 size={28} className="text-teal-400 animate-spin" />
          </div>
        )}

        {access === 'denied' && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className={`max-w-md w-full text-center rounded-2xl border p-10 ${
                isDarkMode ? 'bg-space-800/60 border-white/5' : 'bg-white border-gray-200'
              }`}
              data-testid="affiliates-access-denied"
            >
              <div className={`w-14 h-14 mx-auto mb-5 rounded-2xl flex items-center justify-center ${
                isDarkMode ? 'bg-amber-400/10' : 'bg-amber-500/10'
              }`}>
                <ShieldAlert size={26} className="text-amber-400" />
              </div>
              <h2 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Admin access required
              </h2>
              <p className={`text-sm leading-relaxed mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                The Affiliate Program is available to workspace owners and team members with the Admin role.
                Ask your workspace owner to upgrade your role in Settings → Team.
              </p>
              <button
                onClick={() => navigate(`/workspace/${workspaceId}`)}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-teal-400/10 text-teal-400 border border-teal-400/20 hover:bg-teal-400/20 transition-colors"
                data-testid="affiliates-back-to-dashboard-btn"
              >
                Back to Dashboard
              </button>
            </motion.div>
          </div>
        )}

        {access === 'granted' && embed === 'portal' && <PortalCard isDarkMode={isDarkMode} />}

        {access === 'granted' && embed === 'iframe' && (
          <>
            {!iframeLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  isDarkMode ? 'bg-teal-400/10' : 'bg-teal-500/10'
                }`}>
                  <Handshake size={22} className="text-teal-400" />
                </div>
                <Loader2 size={20} className="text-teal-400 animate-spin" />
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Loading Affiliate Program…
                </p>
              </div>
            )}
            <iframe
              src={AFFILIATES_URL}
              title="Voxinity Affiliate Program"
              onLoad={() => setIframeLoaded(true)}
              allow="clipboard-read; clipboard-write; fullscreen"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
              className={`w-full h-full border-0 transition-opacity duration-300 ${iframeLoaded ? 'opacity-100' : 'opacity-0'}`}
              data-testid="affiliates-iframe"
            />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
